import React, { useRef, useEffect } from 'react';
import { DEFAULT_VOLUME } from '../utils/constants';

export const AudioPlayer = React.memo(({ stream, unitId, volume = DEFAULT_VOLUME }) => {
  const audioRef = useRef(null);
  const currentTrackIdRef = useRef(null);
  const setupInProgressRef = useRef(false);

  const audioTrackId = stream?.getAudioTracks()[0]?.id || null;

  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement) return;

    if (setupInProgressRef.current) return;

    if (!stream) {
      if (audioElement.srcObject) audioElement.srcObject = null;
      currentTrackIdRef.current = null;
      return;
    }

    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) return;

    const trackId = audioTracks[0].id;

    const existingTrackId = audioElement.srcObject?.getAudioTracks()[0]?.id;
    const needsAttach = !audioElement.srcObject || audioElement.srcObject !== stream;
    if (!needsAttach && (currentTrackIdRef.current === trackId || existingTrackId === trackId)) {
      currentTrackIdRef.current = trackId;
      return;
    }

    setupInProgressRef.current = true;
    currentTrackIdRef.current = trackId;

    if (needsAttach) audioElement.srcObject = stream;
    audioElement.volume = volume;
    setupInProgressRef.current = false;

    const handleCanPlay = () => {
      audioElement.play().catch(err => {
        console.warn(`Audio play error for ${unitId}:`, err);
      });
    };

    const handleError = (e) => {
      console.error(`Audio error for ${unitId}:`, e);
    };

    const handlePlaying = () => {
      console.log(`Audio playing: ${unitId}`);
    };

    audioElement.addEventListener('canplay', handleCanPlay);
    audioElement.addEventListener('error', handleError);
    audioElement.addEventListener('playing', handlePlaying);

    return () => {
      audioElement.removeEventListener('canplay', handleCanPlay);
      audioElement.removeEventListener('error', handleError);
      audioElement.removeEventListener('playing', handlePlaying);
    };
  }, [audioTrackId, stream, unitId, volume]);

  useEffect(() => {
    return () => {
      if (audioRef.current) audioRef.current.srcObject = null;
    };
  }, []);

  return (
    <audio
      ref={audioRef}
      autoPlay
      playsInline
      style={{ display: 'none' }}
    />
  );
});
